import { motion } from "framer-motion";
import { ChatCircleText, CalendarBlank, CalendarCheck, CalendarStar, Sparkle } from "@phosphor-icons/react";
import { Reveal } from "@/components/site/Reveal";
import { SMS_LINK, PHONE_DISPLAY, IMAGES } from "@/lib/brand";

const PLANS = [
  { icon: CalendarBlank, term: "Daily", tag: "Short jobs", body: "Perfect for a weekend cleanout, single-day haul, or quick project." },
  { icon: CalendarCheck, term: "Weekly", tag: "Renovations", body: "Keep it on-site all week for remodels, landscaping, and demo work." },
  { icon: CalendarStar, term: "Monthly", tag: "Big projects", body: "Best value for ongoing builds, contractors, and long-haul cleanups." },
];

export const DumpTrailer = () => (
  <section
    id="dump-trailer"
    data-testid="dump-trailer-section"
    className="relative z-10 bg-[var(--tt-black)] px-5 md:px-10 lg:px-16 py-24 md:py-32 border-t border-white/10"
  >
    <Reveal>
      <div className="flex items-center gap-3 mb-6">
        <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-black bg-[var(--tt-green)] px-3 py-1.5 tt-green-glow">
          <Sparkle weight="fill" className="h-3.5 w-3.5" /> New Service
        </span>
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Available Now</span>
      </div>
    </Reveal>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <Reveal>
        <div>
          <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tighter mb-6">
            Dump Trailer <br />
            <span className="text-[var(--tt-green)] tt-text-glow">Rentals.</span>
          </h2>
          <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-md mb-8">
            Heavy-duty hydraulic dump trailer for hire — daily, weekly, or monthly. Cleanouts,
            renovations, landscaping, demo debris and contractor hauls. Load it, dump it, done.
          </p>
          <a
            href={SMS_LINK}
            data-testid="dump-trailer-text-button"
            className="group inline-flex items-center gap-3 bg-[var(--tt-green)] text-black font-display uppercase text-xl md:text-2xl px-9 py-5 border-2 border-[var(--tt-green)] hover:bg-transparent hover:text-[var(--tt-green)] hover:shadow-[0_0_34px_rgba(57,255,20,0.5)] transition-[background-color,color,box-shadow] duration-300 tt-clip-card"
          >
            <ChatCircleText weight="fill" className="h-6 w-6 group-hover:rotate-6 transition-transform" />
            Text for Pricing: {PHONE_DISPLAY}
          </a>
          <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mt-4">
            Rates sent straight to your phone — just text us.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.12}>
        <div className="relative overflow-hidden border border-white/10 tt-clip-hero">
          <img
            src={IMAGES.dumpTrailer}
            alt="Heavy-duty black dump trailer with neon green accents on an industrial lot"
            className="w-full h-[280px] md:h-[380px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--tt-black)]/60 to-transparent" />
        </div>
      </Reveal>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-12 md:mt-16">
      {PLANS.map((p, i) => {
        const Icon = p.icon;
        return (
          <Reveal key={p.term} delay={i * 0.12}>
            <div
              data-testid={`rental-plan-${p.term.toLowerCase()}`}
              className="group relative h-full bg-[var(--tt-surface)] border border-white/5 p-8 hover:border-[var(--tt-green)]/60 transition-colors duration-500 tt-clip-card"
            >
              <div className="flex items-center justify-between mb-8">
                <Icon
                  weight="fill"
                  className="h-9 w-9 text-zinc-600 group-hover:text-[var(--tt-green)] group-hover:tt-green-glow transition-colors duration-500"
                />
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--tt-green)]">
                  {p.tag}
                </span>
              </div>
              <h3 className="font-display uppercase text-3xl tracking-tight mb-3 leading-none">
                {p.term}
              </h3>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed mb-6">{p.body}</p>
              <a
                href={SMS_LINK}
                data-testid={`rental-text-${p.term.toLowerCase()}`}
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white group-hover:text-[var(--tt-green)] transition-colors"
              >
                <ChatCircleText weight="fill" className="h-4 w-4 text-[var(--tt-green)]" />
                Text for {p.term.toLowerCase()} rate →
              </a>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);
